import { isInventoryPracticeActive } from '../../onboarding/practice/inventoryPractice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  primaryText,
  secondaryText,
  tertiaryText,
  semiBoldText,
  smallTextSize,
  selectableName,
} from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';
import { useInventoryViewStore as useInventoryStore } from '../../onboarding/practice/inventoryPractice';
import { InventorySectionProduct } from '../../../services/inventory/inventoryService';
import AssignProductsModal from './AssignProductsModal';
import { walkthroughAddProduct } from '../../onboarding/inventoryValues';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The saved count the product is added to. */
  countId: string;
  sectionId: string;
  sectionName: string;
  restaurantId: string;
  /** Product keys already in the count — those are corrected, not added again. */
  existingKeys: Set<string>;
}

/**
 * Add a product staff forgot to count to an already-saved count. Only products CONFIGURED in
 * this section can be added (the count is a snapshot of that section's product list), so a
 * product that isn't in the section yet is set up from here without leaving the flow: layer 2
 * is the same AssignProductsModal the inventory config page uses (which persists it as config
 * for future counts too), and layer 3 inside it creates a custom ingredient when the product
 * has no order history to link to. The count unit is picked from the assignment's configured
 * units, and the server freezes the line exactly like a fresh save — with today's live price,
 * since a product that was never counted here has no historical price to inherit.
 */
export default function AddCountProductModal({
  isOpen,
  onClose,
  countId,
  sectionId,
  sectionName,
  restaurantId,
  existingKeys,
}: Props) {
  const { t } = useTranslation();
  const addCountProducts = useInventoryStore((s) => s.addCountProducts);
  const loadSectionProducts = useInventoryStore((s) => s.loadSectionProducts);
  const sectionProducts = useInventoryStore((s) => s.sectionProducts[sectionId]);

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<InventorySectionProduct | null>(null);
  const [countValue, setCountValue] = useState('');
  const [unit, setUnit] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Second layer: set the product up in this section first (config for future counts too).
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setSelected(null);
    setCountValue('');
    setUnit('');
    setSaving(false);
    setHasError(false);
    setIsAssignOpen(false);
    void loadSectionProducts(sectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sectionId]);

  // Lock background page scroll while the drawer is open (same idiom as the other
  // inventory side modals).
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // The layer-2 drawer releases the lock when it unmounts while this one is still open —
  // re-apply it (the effect above owns the final release).
  useEffect(() => {
    if (!isOpen || isAssignOpen) return;
    document.body.style.overflow = 'hidden';
  }, [isOpen, isAssignOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isInventoryPracticeActive()) return;
    const onSelect = (e: Event) => {
      const detail = (e as CustomEvent<{ name?: string; key?: string }>).detail;
      const list = (sectionProducts ?? []).filter((p) => !existingKeys.has(p.productKey));
      const match =
        (detail?.key ? list.find((p) => p.productKey === detail.key) : undefined) ??
        (detail?.name
          ? list.find((p) => p.productName.toLowerCase() === detail.name!.toLowerCase()) ??
            list.find((p) => p.productName.toLowerCase().includes(detail.name!.toLowerCase()))
          : undefined);
      if (!match) return;
      handleSelect(match);
      requestAnimationFrame(() => {
        document.querySelector<HTMLElement>('[data-onboarding~="inventory-add-product-pick"]')?.scrollIntoView({ block: 'nearest' });
      });
    };
    const onCount = (e: Event) => {
      const count = (e as CustomEvent<{ count?: string }>).detail?.count;
      if (count == null || count === '') return;
      setCountValue(count);
    };
    const onConfirm = () => {
      void handleSaveRef.current();
    };
    const onCloseDrawer = () => onClose();
    window.addEventListener('onboarding:inventory-add-product-select', onSelect);
    window.addEventListener('onboarding:inventory-add-product-count', onCount);
    window.addEventListener('onboarding:inventory-add-product-confirm', onConfirm);
    window.addEventListener('onboarding:inventory-close-add-product', onCloseDrawer);
    return () => {
      window.removeEventListener('onboarding:inventory-add-product-select', onSelect);
      window.removeEventListener('onboarding:inventory-add-product-count', onCount);
      window.removeEventListener('onboarding:inventory-add-product-confirm', onConfirm);
      window.removeEventListener('onboarding:inventory-close-add-product', onCloseDrawer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sectionProducts, existingKeys]);

  const addableProducts = useMemo(() => {
    const list = (sectionProducts ?? []).filter((p) => !existingKeys.has(p.productKey));
    const q = search.trim().toLowerCase();
    return q ? list.filter((p) => p.productName.toLowerCase().includes(q)) : list;
  }, [sectionProducts, existingKeys, search]);

  const unitOptions = useMemo(() => {
    if (!selected) return [];
    return [
      { unit: selected.countUnit, factor: selected.countUnitToStdFactor },
      ...selected.additionalCountUnits,
    ];
  }, [selected]);

  const handleSelect = (product: InventorySectionProduct) => {
    setSelected(product);
    setUnit(product.countUnit);
  };

  // Back from layer 2: the store already appended the new assignments to this section's
  // product list, so all that is left is picking the first one up for the count.
  const handleAssigned = (inserted: InventorySectionProduct[]) => {
    setSearch('');
    const first = inserted.find((p) => !existingKeys.has(p.productKey));
    if (first) handleSelect(first);
  };

  const countNum = Number(countValue);
  const validCount = countValue.trim() !== '' && Number.isFinite(countNum) && countNum >= 0;

  const handleSave = async () => {
    if (!selected || !validCount || saving) return;
    setSaving(true);
    setHasError(false);
    try {
      await addCountProducts(countId, restaurantId, [
        { productKey: selected.productKey, count: countNum, countUnit: unit || selected.countUnit },
      ]);
      onClose();
    } catch (err) {
      console.error('Failed to add product to inventory count:', err);
      setHasError(true);
      setSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  const walkthroughKey = walkthroughAddProduct()?.productKey;

  useEffect(() => {
    if (!isOpen || !walkthroughKey) return;
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('[data-onboarding~="inventory-add-product-pick"]')?.scrollIntoView({ block: 'nearest' });
    });
  }, [isOpen, walkthroughKey, addableProducts]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/60 dark:bg-black/60 transition-opacity duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        data-onboarding={isOpen ? 'inventory-add-product' : undefined}
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-[584px]',
          'bg-white dark:bg-[#0A2133] shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar with back arrow */}
        <div className="flex items-center px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E8E8E9] dark:border-[#1E3A4E] flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-[#13354F] hover:bg-gray-50 dark:bg-[#13354F] transition-colors"
            aria-label={t('inventory.cancel') as string}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dark:text-[#CBD5E1] text-[#000]" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8337 10H4.16699" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.0003 15.8332L4.16699 9.99984L10.0003 4.1665" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 pt-3 sm:pt-5 pb-3 sm:pb-4 flex flex-col min-h-0">
          <div className="flex flex-col gap-2 sm:gap-3 mb-5 sm:mb-6">
            <h2 className="text-[24px] sm:text-[28px] leading-[36px] sm:leading-[39px] font-semibold sm:font-bold text-[#161924] dark:text-zinc-50">
              {t('inventory.addCountProductTitle')}
            </h2>
            <p className="text-[14px] sm:text-[16px] leading-[18px] sm:leading-[21px] font-normal text-[#475467] dark:text-[#CBD5E1]">
              {t('inventory.addCountProductDescription', { section: sectionName })}
            </p>
          </div>

          {hasError && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">{t('inventory.addCountProductError')}</p>
            </div>
          )}

          {/* Product picker */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085] dark:text-[#CBD5E1] pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('inventory.searchProducts') as string}
              className={cn(
                'w-full pl-10 pr-4 h-11 rounded-lg border dark:border-[#2D4F68] outline-none text-[16px] leading-6 font-normal',
                borderPrimary,
                'bg-white dark:bg-[#14344C] text-zinc-900 dark:text-zinc-50'
              )}
            />
          </div>

          <div className={cn('rounded-lg border overflow-y-auto max-h-[280px]', borderPrimary)} data-onboarding="inventory-add-product-list">
            {addableProducts.length === 0 ? (
              <p className={cn(smallTextSize, tertiaryText, 'py-6 text-center')}>
                {t('inventory.addCountProductNoneLeft')}
              </p>
            ) : (
              addableProducts.map((p) => {
                const isSelected = selected?.productKey === p.productKey;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-onboarding={p.productKey === walkthroughKey ? 'inventory-add-product-pick' : undefined}
                    onClick={() => handleSelect(p)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 border-b last:border-b-0 transition-colors',
                      borderPrimary,
                      isSelected ? 'bg-[#EDFCF5] dark:bg-[#0E89651A]' : 'hover:bg-gray-50 dark:hover:bg-[#14344C]'
                    )}
                  >
                    <span className={cn(primaryText, smallTextSize, semiBoldText, selectableName)}>{p.productName}</span>
                    <span className={cn(secondaryText, 'text-[12px] leading-4')}>
                      {p.pricePerCountUnit != null
                        ? `${currencyFormatter.format(p.pricePerCountUnit)} / ${p.countUnit}`
                        : t('inventory.badgeNoPrice')}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Not configured in this section yet? Set it up from here — it stays as config
              for future counts, exactly as if it had been added on the inventory setup page. */}
          <button
            type="button"
            onClick={() => setIsAssignOpen(true)}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-dashed border-[#0E8965] dark:border-[#28A878] text-[14px] leading-5 font-semibold text-[#0E8965] dark:text-[#28A878] hover:bg-[#EDFCF5] dark:hover:bg-[#0E89651A] transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('inventory.addCountProductNewProduct')}
          </button>

          {/* Count + unit */}
          {selected && (
            <div className="mt-5 sm:mt-6 rounded-lg border border-[#E8E8E9] dark:border-[#1E3A4E] bg-[#F9F9FA] dark:bg-[#0E2B42] p-4 flex flex-col gap-3 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
              <span className="text-[16px] leading-[28px] font-medium text-[#344054] dark:text-[#CBD5E1]">
                {t('inventory.addCountProductCountLabel')} *
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={countValue}
                  data-onboarding="inventory-add-product-count"
                  onChange={(e) => setCountValue(e.target.value)}
                  placeholder="0"
                  autoFocus
                  className={cn(
                    'flex-1 h-11 px-3 rounded-lg border outline-none bg-white dark:bg-[#081C2C]',
                    borderPrimary,
                    'text-[16px] leading-5 font-semibold text-[#111827] dark:text-zinc-50'
                  )}
                />
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  className={cn(
                    'h-11 px-3 rounded-lg border outline-none bg-white dark:bg-[#081C2C]',
                    borderPrimary,
                    'text-[16px] leading-5 font-semibold text-[#111827] dark:text-zinc-50'
                  )}
                  aria-label={t('inventory.addCountProductUnitLabel') as string}
                >
                  {unitOptions.map((u) => (
                    <option key={u.unit} value={u.unit}>
                      {u.unit}
                    </option>
                  ))}
                </select>
              </div>
              <p className={cn('text-xs', secondaryText)}>{t('inventory.addCountProductPriceNote')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 sm:gap-6 sm:px-5 p-4 sm:py-5 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="bg-white flex-1 px-6 py-[11px] rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] disabled:opacity-50 transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]"
          >
            {t('inventory.cancel')}
          </button>
          <button
            onClick={handleSave}
            data-onboarding="inventory-add-product-confirm"
            disabled={saving || !selected || !validCount}
            className="flex-1 px-6 py-[11px] rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? t('inventory.loading') : t('inventory.addCountProductConfirm')}
          </button>
        </div>
      </div>

      {/* Layer 2 — portalled out of this drawer: its `transform` would otherwise confine
          the fixed-positioned panel. Stacked above with a higher z-index. Mounted (closed)
          alongside this drawer rather than on demand, so it slides in instead of popping. */}
      {isOpen && createPortal(
        <AssignProductsModal
          isOpen={isAssignOpen}
          onClose={() => setIsAssignOpen(false)}
          restaurantId={restaurantId}
          sectionId={sectionId}
          sectionName={sectionName}
          onAssigned={handleAssigned}
          zIndexClass="z-[60]"
        />,
        document.body
      )}
    </>
  );
}
